import { ReactNode, useCallback, useContext } from 'react'
import i18next from 'i18next'
import api from '../../../api'
import { ProjectContext } from '../../../contexts'
import { useResolver } from '../../../hooks'
import { Operator, Preferences, Rule } from '../../../types'
import { GroupedRule, isEventWrapper, isTextMatchRule, isWrapper, matchesExactCase, getOperatorTypes, trimPathDisplay } from './RuleHelpers'
import { formatDate } from '../../../utils'

function ListRuleName({ listId }: { listId: number }) {
    const [{ id: projectId }] = useContext(ProjectContext)
    const [list] = useResolver(useCallback(async () => listId ? await api.lists.get(projectId, listId) : null, [projectId, listId]))
    return <>{list?.name ?? `#${listId}`}</>
}

export function ruleDescription(preferences: Preferences, rule: Rule | GroupedRule, nodes: ReactNode[] = [], wrapperOperator?: Operator): ReactNode {
    const root = nodes.length === 0
    const t = i18next.t
    if (isWrapper(rule)) {
        if (isEventWrapper(rule)) {
            if (!root) {
                nodes.push(
                    t('rule_has_user_done') + ' ',
                    <strong key={nodes.length}>
                        {rule.value ?? ''}
                    </strong>,
                )
            } else {
                nodes.push(
                    <strong key={nodes.length}>
                        {rule.value ?? ''}
                    </strong>,
                )
            }
            if (rule.children?.length) {
                nodes.push(' ' + t('rule_where') + ' ')
            }
        }
        if (rule.children?.length) {
            const grouped: GroupedRule[] = []
            for (const child of rule.children) {
                if (child.type === 'wrapper' || child.type === 'list') {
                    grouped.push(child)
                    continue
                }
                const path = trimPathDisplay(child.path)
                const prev = grouped.find(g =>
                    g.group === child.group
                    && trimPathDisplay(g.path) === path
                    && g.operator === child.operator,
                )
                if (prev) {
                    const childValues = Array.isArray(child.value) ? child.value : [child.value ?? '']
                    if (Array.isArray(prev.value)) {
                        prev.value.push(...childValues)
                    } else {
                        prev.value = [prev.value ?? '', ...childValues]
                    }
                } else {
                    grouped.push({ ...child }) // copy so we don't modify original
                }
            }
            grouped.forEach((g, i) => {
                if (i > 0) {
                    nodes.push(', ')
                    if (wrapperOperator) {
                        nodes.push(rule.operator === 'and' ? t('rule_and') + ' ' : t('rule_or') + ' ')
                    }
                }
                ruleDescription(preferences, g, nodes, rule.operator)
            })
        }
        if (isEventWrapper(rule) && rule.frequency) {
            nodes.push(` ${rule.frequency.operator} ${rule.frequency.count} ${t('times')}`)
            if (rule.frequency.period && rule.frequency.period.type === 'rolling') {
                nodes.push(' ' + t('rule_in_the_last') + ' ')
                nodes.push(
                    <strong key={nodes.length}>
                        {rule.frequency.period.value} {rule.frequency.period.unit}{rule.frequency.period.value > 1 ? 's' : ''}
                    </strong>,
                )
            }
        }
    } else if (rule.type === 'list') {
        nodes.push(t(rule.operator === 'none' ? 'rule_user_not_in_list' : 'rule_user_in_list') + ' ')
        nodes.push(
            <strong key={nodes.length}>
                <ListRuleName listId={Number(rule.value) || 0} />
            </strong>,
        )
    } else {
        if (rule.group === 'event' && (rule.path === '$.name' || rule.path === 'name')) {
            nodes.push(t('rule_event') + ' ')
        }
        if (rule.group === 'user') {
            nodes.push(t('rule_user_property') + ' ')
        }
        if (rule.group === 'journey') {
            nodes.push(t('rule_step_data') + ' ')
        }

        nodes.push(
            <code key={nodes.length}>
                {trimPathDisplay(rule.path)}
            </code>,
        )

        nodes.push(' ' + (getOperatorTypes(t)[rule.type]?.find(ot => ot.key === rule.operator)?.label ?? rule.operator))

        if (rule.operator !== 'empty' && rule.operator !== 'is set' && rule.operator !== 'is not set') {
            nodes.push(' ')
            const values = Array.isArray(rule.value) ? rule.value : [rule.value ?? '']
            values.forEach((value, i, a) => {
                if (i > 0) {
                    nodes.push(', ')
                    if (i === a.length - 1 && wrapperOperator) {
                        nodes.push(wrapperOperator === 'and' ? t('rule_and') + ' ' : t('rule_or') + ' ')
                    }
                }
                if (value.includes('{{')) {
                    nodes.push(
                        <code key={nodes.length}>
                            {value}
                        </code>,
                    )
                } else {
                    value = value.trim()
                    if (rule.type === 'boolean') value = 'true'
                    if (rule.type === 'number') {
                        try {
                            if (value.includes('.')) {
                                value = parseFloat(value).toLocaleString()
                            } else {
                                value = parseInt(value, 10).toLocaleString()
                            }
                        } catch {}
                    }
                    if (rule.type === 'date') {
                        try {
                            value = formatDate(preferences, value, 'Ppp')
                        } catch {}
                    }
                    nodes.push(
                        <strong key={nodes.length}>
                            {value}
                        </strong>,
                    )
                }
            })
            if (isTextMatchRule(rule) && matchesExactCase(rule)) {
                nodes.push(' ' + t('rule_exact_case'))
            }
        }
    }
    if (root) {
        return (
            <span className="rule-describe">
                {nodes}
            </span>
        )
    }
    return nodes
}
